import { IDropdownModelString } from '@shared/models/dropdown-string-model';
import { SortedPage } from '@shared/models/sortedPage';
import { AlignmentType } from '@shared/enums/AlignmentType';
import { IContentModel } from '@shared/models/content.model';
import { ITranslation } from '@shared/models/language.model';
import { IPaddingModel } from '@shared/models/padding.model';
import { MenuPositionType } from '@shared/enums/MenuPositionType';
import { ButtonType } from '@shared/enums/ButtonType';
import { ContentContextType } from '@shared/enums/ContentContextType';
import { ITagModel } from '@shared/models/tag.model';
import { IFooterModel } from '../footers-service/footers.model';
import { CopyMode } from '@shared/enums/CopyMode';

export interface IAdvancedTemplateModel {
    id: string;
    name: string;
    description: string;
    tags: ITagModel[];
    backgroundColor: string;
    textColor: string;
    contentColor: string;
    borderColor: string;
    isDefault: boolean;
    customDomainId: string;
    headerSetting: IHeaderSettingModel;
    bannerSetting: IBannerSettingModel;
    contentSetting: IContentSettingModel;
    buttonSetting: IButtonSettingModel;
    footers: IAdvancedTemplateFooterModel[];
}

export class AdvancedTemplateModel implements IAdvancedTemplateModel {
    constructor(
        public id: string,
        public name: string,
        public description: string,
        public tags: ITagModel[],
        public backgroundColor: string,
        public textColor: string,
        public contentColor: string,
        public borderColor: string,
        public isDefault: boolean,
        public customDomainId: string,
        public headerSetting: IHeaderSettingModel,
        public bannerSetting: IBannerSettingModel,
        public contentSetting: IContentSettingModel,
        public buttonSetting: IButtonSettingModel,
        public footers: IAdvancedTemplateFooterModel[]
    ) { }
}

export interface IAdvancedTemplatesForDropdown {
    advancedTemplates: IDropdownModelString[];
}

export class AdvancedTemplatesForDropdown implements IAdvancedTemplatesForDropdown {
    constructor(
        public advancedTemplates: IDropdownModelString[]
    ) { }
}

export interface IAdvancedTemplatesModel {
    advancedTemplates: IAdvancedTemplateModel[];
}

export class AdvancedTemplatesModel implements IAdvancedTemplatesModel {
    constructor(
        public advancedTemplates: IAdvancedTemplateModel[]
    ) { }
}

export class AdvancedTemplatesFilterModel {
    // The advanced template search value
    public searchValue = '';
    // The advanced template tags
    public tagIds = [];
}

export class AdvancedTemplatesPage extends SortedPage {
    public filter: AdvancedTemplatesFilterModel = new AdvancedTemplatesFilterModel();
}

export class AdvancedTemplatesPagedData {
    public advancedTemplates = new Array<AdvancedTemplateModel>();
    public totalElements: number;
    public totalPages: number;
}

export interface IAdvancedTemplateCloneModel {
    id: string;
    name: string;
    tenantIds: string[];
}

export class AdvancedTemplateCloneModel implements IAdvancedTemplateCloneModel {
    constructor(
        public id: string,
        public name: string,
        public tenantIds: string[]
    ) { }
}

export class CustomFieldTemplateCloneModel implements IAdvancedTemplateCloneModel {
    constructor(
        public id: string,
        public name: string,
        public tenantIds: string[]
    ) { }
    cloneToTenants?: boolean;
    copyMode?: CopyMode;
}

export class DocumentCloneModel implements IAdvancedTemplateCloneModel {
    constructor(
        public id: string,
        public name: string,
        public tenantIds: string[]
    ) { }
    cloneToTenants?: boolean;
    copyMode?: CopyMode;
}

export class SurveyCloneModel implements IAdvancedTemplateCloneModel {
    constructor(
        public id: string,
        public name: string,
        public tenantIds: string[]
    ) { }
    cloneToTenants?: boolean;
    copyMode?: CopyMode;
}

export interface IHeaderTextSettingModel {
    backgroundColor: string;
    isPositionSticky: boolean;
    isTopStackOrder: boolean;
    padding: IPaddingModel;
}

export class HeaderTextSettingModel implements IHeaderTextSettingModel {
    constructor(
        public backgroundColor: string,
        public isPositionSticky: boolean,
        public isTopStackOrder: boolean,
        public padding: IPaddingModel
    ) { }
}

export interface IHeaderSettingModel {
    showContent: boolean;
    showText: boolean;
    headerContentSetting: IHeaderContentSettingModel;
    headerTextSetting: IHeaderTextSettingModel;
    contents: IContentModel[];
    translations: ITranslation[];
}

export class HeaderSettingModel implements IHeaderSettingModel {
    constructor(
        public showContent: boolean,
        public showText: boolean,
        public headerContentSetting: IHeaderContentSettingModel,
        public headerTextSetting: IHeaderTextSettingModel,
        public contents: IContentModel[],
        public translations: ITranslation[]
    ) { }
}

export interface IHeaderContentSettingModel {
    backgroundColor: string;
    height: number;
    isFullWidth: boolean;
    isPositionSticky: boolean;
    isTopStackOrder: boolean;
    alignmentType: AlignmentType;
    padding: IPaddingModel;
}

export class HeaderContentSettingModel implements IHeaderContentSettingModel {
    constructor(
        public backgroundColor: string,
        public height: number,
        public isFullWidth: boolean,
        public isPositionSticky: boolean,
        public isTopStackOrder: boolean,
        public alignmentType: AlignmentType,
        public padding: IPaddingModel
    ) { }
}

export interface IBannerSettingModel {
    showContent: boolean;
    backgroundColor: string;
    isPositionSticky: boolean;
    isTopStackOrder: boolean;
    contents: IContentModel[];
}

export class BannerSettingModel implements IBannerSettingModel {
    constructor(
        public showContent: boolean,
        public backgroundColor: string,
        public isPositionSticky: boolean,
        public isTopStackOrder: boolean,
        public contents: IContentModel[]
    ) { }
}

export interface IContentSettingModel {
    width: number;
    marginX: number;
    marginTop: number;
    marginBottom: number;
    isBelowBanner: boolean;
    borderColor: string;
    borderWidth: number;
    borderRadius: number;
    alignmentType: AlignmentType;
    padding: IPaddingModel;
    contentTitleSetting: IContentTitleSettingModel;
    contentBlockSetting: IContentBlockSettingModel;
    contentMenuSetting: IContentMenuSettingModel;
    titleTranslations: ITranslation[];
    blockTranslations: ITranslation[];
}

export class ContentSettingModel implements IContentSettingModel {
    constructor(
        public width: number,
        public marginX: number,
        public marginTop: number,
        public marginBottom: number,
        public isBelowBanner: boolean,
        public borderColor: string,
        public borderWidth: number,
        public borderRadius: number,
        public alignmentType: AlignmentType,
        public padding: IPaddingModel,
        public contentTitleSetting: IContentTitleSettingModel,
        public contentBlockSetting: IContentBlockSettingModel,
        public contentMenuSetting: IContentMenuSettingModel,
        public titleTranslations: ITranslation[],
        public blockTranslations: ITranslation[]
    ) { }
}

export interface IContentTitleSettingModel {
    textColor: string;
    fontFamily: string;
    customFontId: string;
    backgroundColor: string;
    showContentTitle: boolean;
    isAlwaysVisible: boolean;
}

export class ContentTitleSettingModel implements IContentTitleSettingModel {
    constructor(
        public textColor: string,
        public fontFamily: string,
        public customFontId: string,
        public backgroundColor: string,
        public showContentTitle: boolean,
        public isAlwaysVisible: boolean
    ) { }
}

export interface IContentBlockSettingModel {
    textColor: string;
    fontFamily: string;
    customFontId: string;
    backgroundColor: string;
}

export class ContentBlockSettingModel implements IContentBlockSettingModel {
    constructor(
        public textColor: string,
        public fontFamily: string,
        public customFontId: string,
        public backgroundColor: string
    ) { }
}

export interface IContentMenuSettingModel {
    menuPositionType: MenuPositionType;
    alignmentType: AlignmentType;
}

export class ContentMenuSettingModel implements IContentMenuSettingModel {
    constructor(
        public menuPositionType: MenuPositionType,
        public alignmentType: AlignmentType
    ) { }
}

export interface IButtonSettingModel {
    width: number;
    borderRadius: number;
    isFullWidth: boolean;
    fontFamily: string;
    customFontId: string;
    buttonFullSetting: IButtonFullSettingModel;
    buttonHollowSetting: IButtonHollowSettingModel;
    translations: IButtonTranslationModel[];
    alignmentType: AlignmentType;
}

export class ButtonSettingModel implements IButtonSettingModel {
    constructor(
        public width: number,
        public borderRadius: number,
        public isFullWidth: boolean,
        public fontFamily: string,
        public customFontId: string,
        public buttonFullSetting: IButtonFullSettingModel,
        public buttonHollowSetting: IButtonHollowSettingModel,
        public translations: IButtonTranslationModel[],
        public alignmentType: AlignmentType
    ) { }
}

export interface IButtonFullSettingModel {
    buttonColor: IButtonColorModel;
}

export class ButtonFullSettingModel implements IButtonFullSettingModel {
    constructor(
        public buttonColor: IButtonColorModel
    ) { }
}

export interface IButtonHollowSettingModel {
    buttonColor: IButtonColorModel;
}

export class ButtonHollowSettingModel implements IButtonHollowSettingModel {
    constructor(
        public buttonColor: IButtonColorModel
    ) { }
}

export interface IButtonColorModel {
    regularBorderColor: string;
    regularBackgroundColor: string;
    regularTextColor: string;
    hoverBorderColor: string;
    hoverBackgroundColor: string;
    hoverTextColor: string;
}

export class ButtonColorModel implements IButtonColorModel {
    constructor(
        public regularBorderColor: string,
        public regularBackgroundColor: string,
        public regularTextColor: string,
        public hoverBorderColor: string,
        public hoverBackgroundColor: string,
        public hoverTextColor: string
    ) { }
}

export interface IButtonTranslationModel {
    id: string;
    value: string;
    languageId: string;
    buttonType: ButtonType;
}

export class ButtonTranslationModel implements IButtonTranslationModel {
    constructor(
        public id: string,
        public value: string,
        public languageId: string,
        public buttonType: ButtonType
    ) { }
}

export interface IAdvancedTemplateFooterModel {
    index: number;
    footerId: string;
    contentContextType: ContentContextType;
    footer: IFooterModel;
}

export class AdvancedTemplateFooterModel implements IAdvancedTemplateFooterModel {
    constructor(
        public index: number,
        public footerId: string,
        public contentContextType: ContentContextType,
        public footer: IFooterModel
    ) { }
}
